import React, { useEffect, useState } from 'react';
import { useStore } from '../../../../stores/root';
import styles from './styles.module.scss';

// assets

// services

// props
import { IUserResourceShortProps } from '../../../../props/users/user';

// components
import { Persona, PrimaryButton, Stack } from '@fluentui/react';
import Text from '../../../typography/text';
import Heading from '../../../typography/heading';
import Label from '../../../typography/label';
import GeneralService from '../../../../services/general';
import Tag from '../../../uiframeworks/tag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faHome } from '@fortawesome/pro-light-svg-icons';
import UpdatePasswordForm from '../password';
import LogoutForm from '../logout';
import UpdateProfileForm from '../form/update';
import ButtonCards from '../../../buttons/card';
import DetailsStack, { IStackSectionProps } from '../../../uiframeworks/detailsStack';
import MyContractsList from './contracts';
import MyPayslipsList from './payslips';

interface IProfileDetailsProps {
}

const ProfileDetails: React.FC<IProfileDetailsProps> = (props: IProfileDetailsProps) => {
    const { banner, topbar, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [resetPasswordForm, setResetPasswordForm] = useState<JSX.Element>()
    const [logoutForm, setLogoutForm] = useState<JSX.Element>()

    useEffect(() => {
        _onGetProfile();
    }, []);

    const _onGetProfile = async () => {
        try {
            setLoaded(false);

            topbar.show("My Profile", [
                { key: "home", icon: faHome, href: "/" },
                { key: "profile", text: "Profile" }
            ]);

            setLoaded(true);
        } catch (e) {
        }
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        if (refresh) { _onGetProfile() }
    }

    const getTopStack = () => {
        const leftStack: IStackSectionProps[] = [];
        const rightStack: IStackSectionProps[] = [];

        leftStack.push({
            key: 'profile',
            header: { title: 'Profile' },
            isCard: true,
            content: <Stack>
                {user.data ? <Stack tokens={{ childrenGap: 20 }}>
                    <Stack horizontal className={styles.border} verticalAlign='center'>
                        <Stack styles={{ root: { minWidth: 200 } }}>
                            <Label size={'xsmall'}>Profile Picture</Label>
                        </Stack>
                        <Stack grow={1}>
                            <Text size={'small'}>A profile picture helps personalise your account</Text>
                        </Stack>
                        <Persona imageInitials={GeneralService.getInitial(user.data.name)} imageUrl={user.data.imageUrl} />
                    </Stack>
                    <Stack horizontal className={styles.border} verticalAlign='center'>
                        <Stack styles={{ root: { width: 200 } }}>
                            <Label size={'xsmall'}>Name</Label>
                        </Stack>
                        <Stack grow={1}>
                            <Text size={'small'}>{user.data.name}</Text>
                        </Stack>
                    </Stack>
                    <Stack horizontal className={styles.border} verticalAlign='center'>
                        <Stack styles={{ root: { width: 200 } }}>
                            <Label size={'xsmall'}>Email Address</Label>
                        </Stack>
                        <Stack grow={1}>
                            <Text size={'small'}>{user.data.email}</Text>
                        </Stack>
                    </Stack>
                    <Stack horizontal className={styles.border} verticalAlign='center'>
                        <Stack styles={{ root: { width: 200 } }}>
                            <Label size={'xsmall'}>Roles</Label>
                        </Stack>
                        <Stack grow={1} horizontal tokens={{ childrenGap: 10 }}>
                            {user.data.roles.map((role) => {
                                return <Tag key={role.id} text={role.name} />
                            })}
                        </Stack>
                    </Stack>
                    <Stack horizontal className={styles.border} verticalAlign='center'>
                        <Stack styles={{ root: { width: 200 } }}>
                            <Label size={'xsmall'}>Subsidiaries</Label>
                        </Stack>
                        <Stack grow={1} horizontal tokens={{ childrenGap: 10 }}>
                            {(user.data.subsidiaries || []).map((sub) => {
                                return <Tag key={sub.id} text={sub.name} />
                            })}
                        </Stack>
                    </Stack>
                    <Stack horizontal className={styles.border} verticalAlign='center'>
                        <Stack styles={{ root: { width: 200 } }}>
                            <Label size={'xsmall'}>Phone Number</Label>
                        </Stack>
                        <Stack grow={1}>
                            <Text size={'small'}>{user.data.phoneNumber || "-"}</Text>
                        </Stack>
                    </Stack>
                    <Stack horizontal verticalAlign='center'>
                        <Stack styles={{ root: { width: 200 } }}>
                            <Label size={'xsmall'}>Address</Label>
                        </Stack>
                        <Stack grow={1}>
                            <Text size={'small'}>{user.data.address || "-"}</Text>
                        </Stack>
                    </Stack>
                </Stack> : null}
            </Stack>
        });

        rightStack.push({
            key: 'buttons',
            content: <ButtonCards gap={15} buttons={[
                {
                    key: 'updateProfile',
                    text: 'Update profile',
                    variant: 'light',
                    size: 'small',
                    onClick: () => {
                        setActiveSurface('profile.update');
                    }
                },
                {
                    key: 'divider1',
                    variant: 'divider'
                },
                {
                    key: 'changePassword',
                    text: 'Change password',
                    variant: 'light',
                    size: 'small',
                    onClick: () => {
                        setResetPasswordForm(<UpdatePasswordForm onDismissed={function (refresh?: boolean | undefined): void {
                            setResetPasswordForm(undefined)
                        }} />);
                    }
                },
                {
                    key: 'divider2',
                    variant: 'divider'
                },
                {
                    key: 'logout',
                    text: 'Logout',
                    variant: 'light',
                    size: 'small',
                    onClick: () => {
                        setLogoutForm(<LogoutForm onDismissed={function (refresh?: boolean | undefined): void {
                            setLogoutForm(undefined)
                        }} />);
                    }
                }
            ]} />
        })

        return { left: leftStack, right: rightStack }
    }

    const getBottomStack = () => {
        const leftStack: IStackSectionProps[] = [];
        const rightStack: IStackSectionProps[] = [];

        leftStack.push({
            key: 'tabs',
            isCard: true,
            tabs: [
                {
                    key: 'contracts',
                    title: 'Contracts',
                    content: <MyContractsList />
                },
                {
                    key: 'payslips',
                    title: 'Payslips',
                    content: <MyPayslipsList />
                }
            ]
        })
        
        return { left: leftStack, right: rightStack }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {resetPasswordForm}
        {logoutForm}
        <DetailsStack {...getTopStack()} />
        <DetailsStack {...getBottomStack()} />
        {activeSurface === 'profile.update' ? <UpdateProfileForm onDismissed={_onSurfaceDismissed} /> : null}
    </Stack >;
};

export default ProfileDetails;
